<i18n>
{
  "en": {
    "priceEstimationError": "There was an error generating the price profitability estimation"
  },
  "es": {
    "priceEstimationError": "Hubo un error generando la estimación de rentabilidad de precios"
  }
}
</i18n>

<template>
  <div class="d-flex align-center justify-center price-chart">
    <un-front-chart :type="chartType" :chart-data="chartData" :chart-options="chartOptions"
                    :containerStyles="chartStyles"/>
    <div class="d-flex justify-center" v-if="isPriceProfitabilityLoading" :style="spinnerStyles">
      <v-progress-circular indeterminate color="primary" :size="spinnerSize" :width="spinnerWidth"/>
    </div>
    <v-tooltip v-else-if="priceProfitability.isErrored()" top>
      <template v-slot:activator="{ on, attrs }">
         <div class="justify-center" v-on="on" v-bind="attrs" :style="spinnerStyles">
          <v-icon color="red" :size="alertSize">mdi-alert</v-icon>
        </div>
      </template>
      <span>{{ errorText }}</span>
    </v-tooltip>
    <div v-else class="font-weight-black d-flex justify-center align-bottom" :style="labelStyles">
      {{ profitabilityLabel }}
    </div>

  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import UnFrontChart, { CHART_TYPES } from '@ulibs/unfrontcharts';
import { Prop } from 'vue-property-decorator';
import { Components, MapGetters } from '@/apps/core/helpers/decorators';

export const SIZE_TYPES = {
  SMALL: 's',
  MEDIUM: 'md'
};

@Component
@Components({ UnFrontChart })
@MapGetters(['priceProfitability', 'isPriceProfitabilityLoading'])
export default class PriceProfitabilityChart extends Vue {
  @Prop({
    type: String,
    default: SIZE_TYPES.MEDIUM,
    validator: (type) => Object.values(SIZE_TYPES).includes(type)
  }) size;

  get chartType() {
    return CHART_TYPES.HALF_DOUGHNUT;
  }

  get errorText() {
    return this.$t('priceEstimationError');
  }

  get profitabilityLabel() {
    if (!this.priceProfitability.isErrored()) {
      let value;
      if (this.priceProfitability.isNegative()) value = 0.0;
      else value = this.priceProfitability.profitability * 100;
      return `${(value).toFixed(1)}${this.priceProfitability.units}`;
    }
    return '?';
  }

  get chartData() {
    if (this.priceProfitability.isErrored()) {
      return {
        datasets: [{
          backgroundColor: ['#efefef'],
          data: [1],
          borderWidth: 0,
        }]
      };
    }

    const rest = 0.5 - this.priceProfitability.max_profitability;
    const min = this.priceProfitability.min_profitability;
    const profitability = this.priceProfitability.profitability - this.priceProfitability.min_profitability;
    const max = this.priceProfitability.max_profitability - this.priceProfitability.profitability;
    const isNegative = this.priceProfitability.isNegative();

    return {
      datasets: [
        {
          backgroundColor: isNegative ? ['#efefef', '#efefef', '#efefef', '#efefef'] : ['#5de700', '#ff865e', '#00a8ff', '#efefef'],
          data: isNegative ? [0, 0, 0, 0.5] : [min, profitability, max, rest],
          borderWidth: 0,
        }
      ]
    };
  }

  get chartOptions() {
    return {
      cutoutPercentage: 75,
      maintainAspectRatio: true,
      backgroundColor: '#fff',
      legend: {
        display: false
      },
      tooltips: {
        enabled: false
      },
      animation: {
        duration: 0
      }
    };
  }

  get alertSize() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return '40px';
      case SIZE_TYPES.MEDIUM: return '50px';
      default: return '12px';
    }
  }

  get chartStyles() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return this.chartStylesSmall;
      case SIZE_TYPES.MEDIUM: return this.chartStylesMedium;
      default: return this.chartStylesMedium;
    }
  }

  get labelStyles() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return this.labelStylesSmall;
      case SIZE_TYPES.MEDIUM: return this.labelStylesMedium;
      default: return this.labelStylesMedium;
    }
  }

  get spinnerStyles() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return this.spinnerStylesSmall;
      case SIZE_TYPES.MEDIUM: return this.spinnerStylesMedium;
      default: return this.spinnerStylesMedium;
    }
  }

  get spinnerSize() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return 22;
      case SIZE_TYPES.MEDIUM: return 30;
      default: return 30;
    }
  }

  get spinnerWidth() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return 2;
      case SIZE_TYPES.MEDIUM: return 4;
      default: return 4;
    }
  }

  get chartStylesSmall() {
    let chartStyles = {
      height: '101px',
      width: '110px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      chartStyles = {
        height: '92px',
        width: '92px'
      };
    }
    return chartStyles;
  }

  get chartStylesMedium() {
    let chartStyles = {
      height: '210px',
      width: '210px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      chartStyles = {
        ...chartStyles,
        height: '160px',
        width: '160px'
      };
    }
    return chartStyles;
  }

  get spinnerStylesSmall() {
    let spinnerStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '57px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      spinnerStyles = {
        ...spinnerStyles,
        position: 'absolute',
        top: '46px'
      };
    }
    return spinnerStyles;
  }

  get spinnerStylesMedium() {
    let spinnerStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '120px',
      fontSize: '24px',
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      spinnerStyles = {
        ...spinnerStyles,
        top: '86px'
      };
    }
    return spinnerStyles;
  }

  get labelStylesSmall() {
    let labelStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '66px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      labelStyles = {
        ...labelStyles,
        position: 'absolute',
        top: '52px'
      };
    }
    return labelStyles;
  }

  get labelStylesMedium() {
    let labelStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '130px',
      fontSize: '24px',
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      labelStyles = {
        ...labelStyles,
        top: '92px'
      };
    }
    return labelStyles;
  }
}
</script>

<style lang="css" scoped>
.price-chart {
  position: relative;
}
</style>
